import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase-client';
import { Key, Globe, Power, GitBranch as BrandTelegram, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

type ExchangeSettings = {
  exchange_name: string;
  exchange_api_key: string;
  exchange_api_secret: string;
  webhook_url: string;
  trading_enabled: boolean;
};

const ExchangeSettings = () => {
  const [settings, setSettings] = useState<ExchangeSettings>({
    exchange_name: '',
    exchange_api_key: '',
    exchange_api_secret: '',
    webhook_url: '',
    trading_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('trading_account, trading_enabled')
        .eq('id', user.id)
        .single();

      if (error) {
        if (error.code === '42703') {
          console.warn('Some columns might be missing, using default values');
          return;
        }
        throw error;
      }
      
      if (profile) {
        setSettings({
          exchange_name: profile.trading_account?.exchange || '',
          exchange_api_key: profile.trading_account?.api_key || '',
          exchange_api_secret: '', // API secret is never loaded from DB
          webhook_url: profile.trading_account?.webhook_url || '',
          trading_enabled: profile.trading_enabled || false
        });
      }
    } catch (error: any) {
      toast.error('Failed to load exchange settings');
      console.error('Error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Validate webhook URL if provided
      if (settings.webhook_url && !isValidWebhookUrl(settings.webhook_url)) {
        throw new Error('Invalid webhook URL format');
      }

      // Validate API credentials
      if (settings.trading_enabled) {
        if (!settings.exchange_api_key || !settings.exchange_api_secret) {
          throw new Error('Both API key and secret are required to enable trading');
        }
      }

      // First update profile settings
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          trading_account: {
            exchange: settings.exchange_name || 'bybit',
            api_key: settings.exchange_api_key || null,
            api_secret: settings.exchange_api_secret || null,
            webhook_url: settings.webhook_url || null
          },
          trading_enabled: settings.trading_enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // If trading is enabled, update bot statuses
      if (settings.trading_enabled) {
        const { data: selectedBots, error: botsError } = await supabase
          .from('user_selections')
          .select('bot_id')
          .eq('user_id', user.id);

        if (botsError) throw botsError;

        if (selectedBots && selectedBots.length > 0) {
          const { error: statusError } = await supabase
            .from('user_bot_status')
            .upsert(
              selectedBots.map(({ bot_id }) => ({
                user_id: user.id,
                bot_id,
                is_trading: settings.trading_enabled,
                updated_at: new Date().toISOString()
              })),
              { onConflict: 'user_id,bot_id' }
            );

          if (statusError) throw statusError;
        }
      }

      toast.success('Exchange settings updated successfully');
      
      // Reload settings to confirm update
      await loadSettings();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update exchange settings');
      console.error('Error:', error.message);
      
      // Revert trading_enabled state if there was an error
      setSettings(prev => ({
        ...prev,
        trading_enabled: !prev.trading_enabled
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const isValidWebhookUrl = (url: string): boolean => {
    try {
      const webhookUrl = new URL(url);
      return webhookUrl.protocol === 'https:';
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-blue-900/40 backdrop-blur-sm shadow rounded-lg p-6">
      <h3 className="text-xl font-semibold text-white mb-6">Exchange Settings</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-200">
            Exchange
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Globe className="h-5 w-5 text-gray-400" />
            </div>
            <select
              name="exchange_name"
              value={settings.exchange_name}
              onChange={handleChange}
              className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 bg-blue-900/40 rounded-md shadow-sm text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
            >
              <option value="">Select your exchange</option>
              <option value="binance" className="bg-blue-900">Binance</option>
              <option value="bybit" className="bg-blue-900">Bybit</option>
              <option value="okx" className="bg-blue-900">OKX</option>
              <option value="kucoin" className="bg-blue-900">KuCoin</option>
              <option value="mexc" className="bg-blue-900">MEXC</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200">
            API Key
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Key className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              name="exchange_api_key"
              value={settings.exchange_api_key}
              onChange={handleChange}
              className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 bg-blue-900/40 rounded-md shadow-sm text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
              placeholder="Enter your API key"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200">
            API Secret
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Key className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              name="exchange_api_secret"
              value={settings.exchange_api_secret}
              onChange={handleChange}
              className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 bg-blue-900/40 rounded-md shadow-sm text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
              placeholder="Enter your API secret"
            />
          </div>
          <p className="mt-1 text-sm text-gray-400">
            Note: API secret is required for trading but never stored in our database
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200">
            Webhook URL
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <BrandTelegram className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="webhook_url"
              value={settings.webhook_url}
              onChange={handleChange}
              className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-600 bg-blue-900/40 rounded-md shadow-sm text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
              placeholder="Enter webhook URL (https://...)"
            />
          </div>
          <p className="mt-1 text-sm text-gray-400">
            This webhook will receive trading signals from your selected bots
          </p>
        </div>

        {/* API Requirements Notice */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300">
            <p className="font-medium text-blue-400 mb-1">API Requirements</p>
            <p>To enable trading, please ensure your API key has the following permissions:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Read account information</li>
              <li>Read positions</li>
              <li>Place and manage orders</li>
              <li>Transfer funds between accounts</li>
            </ul>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative inline-block w-10 mr-2 align-middle select-none">
            <input
              type="checkbox"
              name="trading_enabled"
              id="trading_enabled"
              checked={settings.trading_enabled}
              onChange={handleChange}
              className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
            />
            <label
              htmlFor="trading_enabled"
              className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                settings.trading_enabled ? 'bg-cyan-500' : 'bg-gray-600'
              }`}
            />
          </div>
          <label htmlFor="trading_enabled" className="text-sm text-gray-200 flex items-center">
            <Power className={`h-4 w-4 mr-2 ${settings.trading_enabled ? 'text-cyan-400' : 'text-gray-400'}`} />
            Enable Trading
          </label>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      <style>{`
        .toggle-checkbox:checked {
          right: 0;
          border-color: #06b6d4;
        }
        .toggle-label {
          transition: background-color 0.2s ease-in;
        }
        .toggle-checkbox {
          transition: all 0.2s ease-in;
          border-color: #4b5563;
          right: 4px;
        }
      `}</style>
    </div>
  );
};

export default ExchangeSettings;
